import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { observeElementResize } from '@this/src/util/browser/resize_observer.util';

const SCROLL_BUTTON_OVERFLOW = '10px';
const GRADIENT_FADE_OPACITY = 0.28;
const GRADIENT_WIDTH_RIGHT = '32px';
const GRADIENT_WIDTH_LEFT = '28px';

export type ScrollHintProps = {
  children: React.ReactNode;
  outerStyle?: React.CSSProperties;
  scrollStyle?: React.CSSProperties;
  scrollProps?: React.HTMLAttributes<HTMLDivElement>;
  fadeColor?: string;
  buttonOverflow?: string;
  showScrollControls?: boolean;
  outerRef?: React.Ref<HTMLDivElement>;
};

export const ScrollHint = ({
  children,
  outerStyle,
  scrollStyle,
  scrollProps,
  fadeColor = '#ffffff',
  buttonOverflow = SCROLL_BUTTON_OVERFLOW,
  showScrollControls = true,
  outerRef
}: ScrollHintProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hadOverflowRef = useRef(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = element;
    const hasOverflow = scrollWidth > clientWidth + 2;
    if (hadOverflowRef.current && !hasOverflow) {
      element.scrollLeft = 0;
    }
    hadOverflowRef.current = hasOverflow;
    const showControls = showScrollControls && hasOverflow;
    const nextCanScrollRight = showControls && scrollLeft + clientWidth < scrollWidth - 2;
    const nextCanScrollLeft = showControls && scrollLeft > 2;
    setCanScrollRight(prev => (prev === nextCanScrollRight ? prev : nextCanScrollRight));
    setCanScrollLeft(prev => (prev === nextCanScrollLeft ? prev : nextCanScrollLeft));
  }, [showScrollControls]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return undefined;
    }
    updateScrollState();
    const disconnectResizeObserver = observeElementResize(element, updateScrollState);
    element.addEventListener('scroll', updateScrollState, { passive: true });
    return () => {
      disconnectResizeObserver();
      element.removeEventListener('scroll', updateScrollState);
    };
  }, [updateScrollState, children, showScrollControls]);

  const handleScroll = (direction: 'left' | 'right') => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const step = Math.max(element.clientWidth * 0.75, 120);
    element.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' });
  };

  const styles = useMemo(
    () => getStyles(canScrollRight, canScrollLeft, fadeColor, buttonOverflow, showScrollControls),
    [canScrollRight, canScrollLeft, fadeColor, buttonOverflow, showScrollControls]
  );

  return (
    <div ref={outerRef} style={{ ...styles.outer, ...outerStyle }}>
      <style>{'[data-scroll-hint-scroll]::-webkit-scrollbar{display:none}'}</style>
      <div ref={scrollRef} data-scroll-hint-scroll style={{ ...styles.scroll, ...scrollStyle }} {...scrollProps}>
        {children}
      </div>
      {canScrollLeft && (
        <>
          <div style={styles.gradientLeft} aria-hidden="true" />
          <button
            type="button"
            style={{ ...styles.scrollButton, ...styles.scrollButtonLeft }}
            aria-label="左にスクロール"
            onClick={() => handleScroll('left')}
          >
            <ChevronLeftIcon style={styles.scrollIcon} />
          </button>
        </>
      )}
      {canScrollRight && (
        <>
          <div style={styles.gradientRight} aria-hidden="true" />
          <button
            type="button"
            style={{ ...styles.scrollButton, ...styles.scrollButtonRight }}
            aria-label="右にスクロール"
            onClick={() => handleScroll('right')}
          >
            <ChevronRightIcon style={styles.scrollIcon} />
          </button>
        </>
      )}
    </div>
  );
};

function getStyles(
  canScrollRight: boolean,
  canScrollLeft: boolean,
  fadeColor: string,
  buttonOverflow: string,
  showScrollControls: boolean
) {
  const gradient = (direction: 'left' | 'right') => {
    const fade = `color-mix(in srgb, ${fadeColor} ${Math.round(GRADIENT_FADE_OPACITY * 100)}%, transparent)`;
    return direction === 'left'
      ? `linear-gradient(to right, ${fade} 0%, transparent 72%)`
      : `linear-gradient(to left, ${fade} 0%, transparent 72%)`;
  };

  return {
    outer: {
      position: 'relative',
      minWidth: 0,
      overflow: 'visible'
    } as const,
    scroll: {
      overflowX: showScrollControls ? 'auto' : 'hidden',
      WebkitOverflowScrolling: showScrollControls ? 'touch' : undefined,
      scrollbarWidth: showScrollControls ? 'none' : undefined,
      msOverflowStyle: showScrollControls ? 'none' : undefined
    } as const,
    gradientRight: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      width: GRADIENT_WIDTH_RIGHT,
      background: gradient('right'),
      pointerEvents: 'none',
      opacity: canScrollRight ? 1 : 0,
      transition: 'opacity 0.2s ease'
    } as const,
    gradientLeft: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: GRADIENT_WIDTH_LEFT,
      background: gradient('left'),
      pointerEvents: 'none',
      opacity: canScrollLeft ? 1 : 0,
      transition: 'opacity 0.2s ease'
    } as const,
    scrollButton: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      padding: 0,
      border: '1px solid #d6dde8',
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.95)',
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
      color: 'var(--link-color, #af985e)',
      cursor: 'pointer'
    } as const,
    scrollButtonLeft: {
      left: `calc(-1 * ${buttonOverflow})`
    } as const,
    scrollButtonRight: {
      right: `calc(-1 * ${buttonOverflow})`
    } as const,
    scrollIcon: {
      fontSize: '22px'
    } as const
  };
}
